import { Alert, Divider, Skeleton, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import { useMemo } from 'react';

import { SelectOption, UserAffiliations } from '@jebel/constants';
import { createFilterBuilder } from '@jebel/utils';

import { SelectAffiliationField, SwitchField } from 'shared/components/form';
import { FormTextField, LoadingOverlay } from 'shared/components/ui';
import { UserFilter } from 'shared/graphql';
import { useHasSession, useUsersListCount } from 'shared/hooks';

import { Container, Section, SectionTitle } from './ContentRestrictionForm.styles';

export interface ContentRestrictionsFormValues {
  description: string;

  isActive: boolean;

  audience: {
    affiliations?: SelectOption<UserAffiliations>[];
  };

  scope: {
    creatorAffiliations?: SelectOption<UserAffiliations>[];
  };
}

export const DEFAULT_CONTENT_RESTRICTION_FORM_VALUES: ContentRestrictionsFormValues = {
  description: '',
  isActive: false,

  audience: {
    affiliations: [],
  },

  scope: {
    creatorAffiliations: [],
  },
};

export function ContentRestrictionsForm() {
  const hasSession = useHasSession();

  const { values } = useFormikContext<ContentRestrictionsFormValues>();

  const audienceFilter = useMemo(() => {
    const filter = createFilterBuilder<UserFilter>();

    if (values.audience.affiliations && values.audience.affiliations.length > 0) {
      const affiliations = values.audience.affiliations.map(a => a.value);
      // Filter by affiliations of the audience
      filter.and({ affiliation: { in: affiliations } });
    }

    return filter.build();
  }, [values]);

  const { count: totalCount, loading: totalCountLoading } = useUsersListCount({
    skip: !hasSession,
    fetchPolicy: 'cache-first',
  });

  const { count: affectedCount, loading: affectedCountLoading } = useUsersListCount({
    skip: !hasSession,
    fetchPolicy: 'cache-first',
    variables: {
      filter: audienceFilter,
    },
  });

  const audienceLoading = affectedCountLoading || totalCountLoading;

  const audiencePercent = useMemo(() => {
    return (affectedCount / totalCount) * 100;
  }, [affectedCount, totalCount]);

  return (
    <Container>
      <FormTextField name="description" label="Description" required />

      <SwitchField name="isActive" label={values.isActive ? 'Enabled' : 'Disabled'} />

      {values.isActive && (
        <Alert severity="warning">
          <strong>WARNING</strong>: Avoid setting overlapping or conflicting restrictions, as this
          may unintentionally block some members from accessing all content.
        </Alert>
      )}

      <Section>
        <SectionTitle>Target Audience</SectionTitle>

        <Typography color="textSecondary">
          Select the Community Affiliation you would like to restrict:
        </Typography>

        <SelectAffiliationField name="audience.affiliations" label="Affiliations" multiple />

        <Alert severity="info">
          <Typography>
            <Typography component="span">
              This restriction will affect approximately&nbsp;
            </Typography>

            <LoadingOverlay
              loading={audienceLoading}
              overlay={
                <Skeleton component="span" variant="text" style={{ display: 'inline-block' }}>
                  <Typography component="span">100%</Typography>
                </Skeleton>
              }
            >
              <Typography component="span" fontWeight={600}>
                {audiencePercent.toFixed(1)}%
              </Typography>
            </LoadingOverlay>

            <Typography component="span">&nbsp;</Typography>

            <LoadingOverlay
              loading={audienceLoading}
              overlay={
                <Skeleton component="span" variant="text" style={{ display: 'inline-block' }}>
                  <Typography component="span">100%</Typography>
                </Skeleton>
              }
            >
              <Typography component="span">
                ({affectedCount} / {totalCount})
              </Typography>
            </LoadingOverlay>

            <Typography component="span">&nbsp;total users.</Typography>
          </Typography>
        </Alert>
      </Section>

      <Divider />

      <Section>
        <SectionTitle>Allowed Content</SectionTitle>

        <Typography color="textSecondary">
          Community Members who match the Audience criteria above will <strong>ONLY</strong> be able
          to view content posted by:
        </Typography>

        <SelectAffiliationField
          name="scope.creatorAffiliations"
          label="Allowed Content by Author Affiliation"
          multiple
        />
      </Section>
    </Container>
  );
}
